import React, { useState, useEffect } from "react";
import { Keypair } from "@solana/web3.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Loader2,
  Plus,
  ExternalLink,
  Check,
  X,
  Eye,
  Send,
  Users,
  Coins,
} from "lucide-react";
import { useAirdrop } from "../hooks/useAirdrop";
import { AirdropApplication, AirdropCampaign } from "../lib/airdropService";
import {
  AirdropMintDistributor,
  DistributionRecipient,
} from "../lib/airdropMintDistributor";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { useToast } from "../hooks/use-toast";
import { PrivateKeyDialog } from "./PrivateKeyDialog";
import { Modal } from "react-bootstrap";

export const AdminAirdropManagement = () => {
  const { publicKey } = useWallet();
  const { connection } = useConnection();
  const { toast } = useToast();
  const {
    applications,
    campaigns,
    activeCampaign,
    loadingApplications,
    loadingCampaigns,
    loadApplications,
    updateApplicationStatus,
    createCampaign,
    updateCampaignData,
    distributeAirdrop,
  } = useAirdrop();

  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [selectedApplications, setSelectedApplications] = useState([]);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    description: "",
    tokenAmount: 0,
    totalSupply: 0,
    isActive: true,
    requirements: [""],
  });
  const [distributionAmount, setDistributionAmount] = useState(0);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [selectedTab, setSelectedTab] = useState("applications");
  const [isDistributing, setIsDistributing] = useState(false);
  const [showPrivateKeyDialog, setShowPrivateKeyDialog] = useState(false);
  const [pendingDistribution, setPendingDistribution] = useState(null);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleCreateCampaign = async () => {
    try {
      setIsCreatingCampaign(true);
      const campaignData = {
        ...newCampaign,
        requirements: newCampaign.requirements.filter(
          (req) => req.trim() !== ""
        ),
      };
      await createCampaign(campaignData);
      setNewCampaign({
        name: "",
        description: "",
        tokenAmount: 0,
        totalSupply: 0,
        isActive: true,
        requirements: [""],
      });
    } catch (error) {
      // Error handled in hook
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  const handleStatusUpdate = async (applicationId, status) => {
    await updateApplicationStatus(applicationId, status);
  };

  const handleBulkDistribute = async () => {
    if (selectedApplications.length === 0 || distributionAmount <= 0) return;

    // Get approved applications for distribution
    const selectedApps = applications.filter(
      (app) =>
        selectedApplications.includes(app.id) && app.status === "approved"
    );

    if (selectedApps.length === 0) {
      toast({
        title: "No Valid Applications",
        description: "Please select approved applications for distribution.",
        variant: "destructive",
      });
      return;
    }

    // Prepare distribution recipients
    const recipients = selectedApps.map((app) => ({
      walletAddress: app.walletAddress,
      amount: distributionAmount,
      applicationId: app.id,
    }));

    // Check for required configuration
    const mintAddress = process.env.REACT_APP_SPL_TOKEN_MINT_ADDRESS;
    const enableRealDistribution =
      process.env.REACT_APP_ENABLE_REAL_DISTRIBUTION === "true";

    if (!mintAddress) {
      toast({
        title: "Configuration Error",
        description:
          "Token mint address not configured. Please set REACT_APP_SPL_TOKEN_MINT_ADDRESS in your environment.",
        variant: "destructive",
      });
      return;
    }

    if (!connection) {
      toast({
        title: "Connection Error",
        description:
          "Solana connection not available. Please check your network settings.",
        variant: "destructive",
      });
      return;
    }

    if (enableRealDistribution) {
      // Store recipients and show private key dialog for real distribution
      setPendingDistribution(recipients);
      setShowPrivateKeyDialog(true);
    } else {
      // Demo mode - proceed without private key
      await executeDistribution(recipients, null);
    }
  };

  const executeDistribution = async (recipients, mintAuthorityKeypair) => {
    try {
      setIsDistributing(true);

      const mintAddress = process.env.REACT_APP_SPL_TOKEN_MINT_ADDRESS;
      const enableRealDistribution =
        process.env.REACT_APP_ENABLE_REAL_DISTRIBUTION === "true";
      const txSignatures = {};

      if (enableRealDistribution && mintAuthorityKeypair) {
        // REAL MINTING MODE
        try {
          console.log("Real Minting Parameters:", {
            mintAddress,
            recipients: recipients.length,
            totalAmount: recipients.reduce((sum, r) => sum + r.amount, 0),
            mintAuthorityPubkey: mintAuthorityKeypair.publicKey.toString(),
          });

          // Initialize the mint distributor
          const distributor = new AirdropMintDistributor(
            connection,
            mintAddress,
            mintAuthorityKeypair
          );

          // Validate minting before proceeding
          const validation = await distributor.validateMinting(recipients);
          if (!validation.valid) {
            toast({
              title: "Minting Validation Failed",
              description: validation.errors.join(", "),
              variant: "destructive",
            });
            return;
          }

          // Show validation info
          console.log("Minting Validation:", validation);

          toast({
            title: "Starting Token Minting",
            description: `Minting ${validation.totalToMint} tokens to ${recipients.length} recipients...`,
            variant: "default",
          });

          // Perform the actual token minting
          const results = await distributor.mintToRecipients(recipients);

          // Process results
          const errors = [];

          results.forEach((result) => {
            if (result.success) {
              txSignatures[result.applicationId] = result.txSignature;
            } else {
              errors.push(
                `❌ Failed to mint to ${result.applicationId}: ${result.error}`
              );
              console.error(`Failed minting:`, result);
            }
          });

          if (errors.length > 0) {
            console.warn("Some minting operations failed:", errors);
            toast({
              title: "Partial Minting Success",
              description: `${results.length - errors.length}/${
                results.length
              } minting operations succeeded. Check console for details.`,
              variant: "default",
            });
          }
        } catch (error) {
          console.error("Real minting error:", error);
          toast({
            title: "Minting Failed",
            description:
              error instanceof Error
                ? error.message
                : "Real token minting failed. Check console for details.",
            variant: "destructive",
          });
          return;
        }
      } else {
        // DEMO MODE: Simulate realistic transaction signatures
        console.log("Demo Minting Mode - To enable real minting:");
        console.log("- Set REACT_APP_ENABLE_REAL_DISTRIBUTION=true");
        console.log("- Provide private key when prompted");

        console.log("Demo Minting Parameters:", {
          mintAddress,
          recipients: recipients.length,
          totalAmount: recipients.reduce((sum, r) => sum + r.amount, 0),
          adminWallet: publicKey?.toString(),
        });

        for (const recipient of recipients) {
          // Simulate network delay
          await new Promise((resolve) =>
            setTimeout(resolve, 200 + Math.random() * 300)
          );

          // Generate realistic Solana transaction signature format
          const chars =
            "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
          let signature = "";
          for (let i = 0; i < 88; i++) {
            signature += chars.charAt(Math.floor(Math.random() * chars.length));
          }

          txSignatures[recipient.applicationId] = signature;

          console.log(
            `Demo: Minted ${recipient.amount} tokens to ${recipient.walletAddress} (TX: ${signature})`
          );
        }

        toast({
          title: "Demo Mode Active",
          description:
            "Simulated token minting completed. Set REACT_APP_ENABLE_REAL_DISTRIBUTION=true for real minting.",
          variant: "default",
        });
      }

      // Update Firebase with transaction signatures
      await distributeAirdrop(
        selectedApplications,
        distributionAmount,
        txSignatures
      );

      // Clear selections
      setSelectedApplications([]);
      setDistributionAmount(0);

      toast({
        title: "Distribution Completed",
        description: `Successfully distributed ${distributionAmount} tokens to ${recipients.length} recipients. Check the applications tab to see transaction details.`,
      });
    } catch (error) {
      console.error("Distribution error:", error);
      toast({
        title: "Distribution Failed",
        description:
          error instanceof Error
            ? error.message
            : "Failed to distribute tokens. Check console for details.",
        variant: "destructive",
      });
    } finally {
      setIsDistributing(false);
      // Clear pending distribution
      setPendingDistribution(null);
    }
  };

  const handlePrivateKeyConfirm = async (keypair) => {
    if (pendingDistribution) {
      await executeDistribution(pendingDistribution, keypair);
    }
  };

  const handleToggleCampaign = async (campaignId, isActive) => {
    await updateCampaignData(campaignId, { isActive });
  };

  const addRequirement = () => {
    setNewCampaign((prev) => ({
      ...prev,
      requirements: [...prev.requirements, ""],
    }));
  };

  const updateRequirement = (index, value) => {
    setNewCampaign((prev) => ({
      ...prev,
      requirements: prev.requirements.map((req, i) =>
        i === index ? value : req
      ),
    }));
  };

  const removeRequirement = (index) => {
    setNewCampaign((prev) => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index),
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-blue-100 text-blue-800";
      case "distributed":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const pendingApplications = applications.filter(
    (app) => app.status === "pending"
  );
  const approvedApplications = applications.filter(
    (app) => app.status === "approved"
  );
  const distributedApplications = applications.filter(
    (app) => app.status === "distributed"
  );

  return (
    <div className="section_03">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="row">
          <div className="col-md-3">
            <Card className="card_01">
              <CardContent>
                <div className="d-flex align-item-center gap-4">
                  <Users className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium">Total Applications</p>
                    <p className="text-2xl font-bold">{applications.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="col-md-3">
            <Card className="card_01">
              <CardContent>
                <div className="d-flex align-item-center gap-4">
                  <div className="h-5 w-5 bg-yellow-500 rounded-full" />
                  <div>
                    <p className="text-sm font-medium">Pending</p>
                    <p className="text-2xl font-bold">
                      {pendingApplications.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="col-md-3">
            <Card className="card_01">
              <CardContent>
                <div className="d-flex align-item-center gap-4">
                  <div className="h-5 w-5 bg-green-500 rounded-full" />
                  <div>
                    <p className="text-sm font-medium">Distributed</p>
                    <p className="text-2xl font-bold">
                      {distributedApplications.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="col-md-3">
            <Card className="card_01">
              <CardContent>
                <div className="d-flex align-item-center gap-4">
                  <Coins className="h-5 w-5 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium">Active Campaign</p>
                    <p className="text-lg font-bold">
                      {activeCampaign?.name || "None"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="tabs-section-wrapper">
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="tabs-list">
              <TabsTrigger value="applications">Applications</TabsTrigger>
              <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
              <TabsTrigger value="distribute">Distribute</TabsTrigger>
            </TabsList>

            {/* Applications Tab */}
            <TabsContent
              value="applications"
              className="space-y-4 table-content-wrapper card_01"
            >
              <Card>
                <CardHeader>
                  <CardTitle>Airdrop Applications</CardTitle>
                  <CardDescription>
                    Manage and review airdrop applications from users
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingApplications ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="ml-2">Loading applications...</span>
                    </div>
                  ) : applications.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No applications found
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Wallet Address</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Applied Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {applications.map((application) => (
                          <TableRow key={application.id}>
                            <TableCell className="font-mono text-xs">
                              {application.walletAddress.slice(0, 8)}...
                              {application.walletAddress.slice(-8)}
                            </TableCell>
                            <TableCell>
                              {application.userEmail || "N/A"}
                            </TableCell>
                            <TableCell>
                              {application.appliedAt
                                .toDate()
                                .toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={getStatusColor(application.status)}
                              >
                                {application.status.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {application.status === "pending" && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleStatusUpdate(
                                          application.id,
                                          "approved"
                                        )
                                      }
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleStatusUpdate(
                                          application.id,
                                          "rejected"
                                        )
                                      }
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                {application.txSignature && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      window.open(
                                        `https://solscan.io/tx/${application.txSignature}`,
                                        "_blank"
                                      )
                                    }
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Campaigns Tab */}
            <TabsContent
              value="campaigns"
              className="space-y-4 table-content-wrapper card_01"
            >
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <CardTitle>Airdrop Campaigns</CardTitle>
                      <CardDescription>
                        Manage airdrop campaigns
                      </CardDescription>
                    </div>

                    <Button className="btn-style" onClick={handleShow}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Campaign
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {campaigns.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No campaigns found
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {campaigns.map((campaign) => (
                        <Card
                          key={campaign.id}
                          className={
                            campaign.isActive ? "border-green-200" : ""
                          }
                        >
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-semibold flex items-center gap-2">
                                  {campaign.name}
                                  {campaign.isActive && (
                                    <Badge className="bg-green-100 text-green-800">
                                      Active
                                    </Badge>
                                  )}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  {campaign.description}
                                </p>
                                <div className="mt-2 text-sm space-y-1">
                                  <p>
                                    Token Amount:{" "}
                                    {campaign.tokenAmount.toLocaleString()} per
                                    user
                                  </p>
                                  <p>
                                    Total Supply:{" "}
                                    {campaign.totalSupply.toLocaleString()}
                                  </p>
                                  <p>
                                    Distributed:{" "}
                                    {campaign.distributedAmount.toLocaleString()}
                                  </p>
                                  <p>
                                    Remaining:{" "}
                                    {(
                                      campaign.totalSupply -
                                      campaign.distributedAmount
                                    ).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Switch
                                  checked={campaign.isActive}
                                  onCheckedChange={(checked) =>
                                    handleToggleCampaign(campaign.id, checked)
                                  }
                                />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Distribute Tab */}
            <TabsContent
              value="distribute"
              className="space-y-4 table-content-wrapper card_01"
            >
              <Card>
                <CardHeader>
                  <CardTitle>Distribute Airdrop</CardTitle>
                  <CardDescription>
                    Select approved applications and distribute tokens
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-4 items-end">
                    <div className="space-y-2 form-group">
                      <Label htmlFor="distributionAmount">
                        Amount per Recipient
                      </Label>
                      <Input
                        id="distributionAmount"
                        type="number"
                        value={distributionAmount}
                        onChange={(e) =>
                          setDistributionAmount(Number(e.target.value))
                        }
                        placeholder="100"
                      />
                    </div>
                    <Button
                      onClick={handleBulkDistribute}
                      disabled={
                        selectedApplications.length === 0 ||
                        distributionAmount <= 0
                      }
                      className="btn-style"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Distribute to {selectedApplications.length} Recipients
                    </Button>
                  </div>

                  {approvedApplications.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No approved applications available for distribution
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <input
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedApplications(
                                    approvedApplications.map((app) => app.id)
                                  );
                                } else {
                                  setSelectedApplications([]);
                                }
                              }}
                              checked={
                                selectedApplications.length ===
                                approvedApplications.length
                              }
                            />
                          </TableHead>
                          <TableHead>Wallet Address</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Applied Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {approvedApplications.map((application) => (
                          <TableRow key={application.id}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedApplications.includes(
                                  application.id
                                )}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedApplications((prev) => [
                                      ...prev,
                                      application.id,
                                    ]);
                                  } else {
                                    setSelectedApplications((prev) =>
                                      prev.filter((id) => id !== application.id)
                                    );
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {application.walletAddress.slice(0, 8)}...
                              {application.walletAddress.slice(-8)}
                            </TableCell>
                            <TableCell>
                              {application.userEmail || "N/A"}
                            </TableCell>
                            <TableCell>
                              {application.appliedAt
                                .toDate()
                                .toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
        <PrivateKeyDialog
          open={showPrivateKeyDialog}
          onOpenChange={setShowPrivateKeyDialog}
          onConfirm={handlePrivateKeyConfirm}
          title="Enter Mint Authority Private Key"
          description="To proceed with real token distribution, please enter your mint authority private key. This key will be processed locally and never stored."
        />

        <Modal
          show={show}
          onHide={handleClose}
          size="xl"
          centered
          className="create-compaign-modal"
        >
          <Modal.Header>
            <Modal.Title>Create New Airdrop Campaign</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">
              Set up a new airdrop campaign for your token distribution
            </p>

            <div className="space-y-4 form-wrapper">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="space-y-2 form-group">
                    <label htmlFor="name">Campaign Name</label>
                    <input
                      id="name"
                      className="form-control"
                      value={newCampaign.name}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder="e.g., Genesis Airdrop"
                    />
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="space-y-2 form-group">
                    <label htmlFor="tokenAmount">Token Amount per User</label>
                    <input
                      id="tokenAmount"
                      type="number"
                      className="form-control"
                      value={newCampaign.tokenAmount}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          tokenAmount: Number(e.target.value),
                        }))
                      }
                      placeholder="100"
                    />
                  </div>
                </div>
                <div className="col-md-12 mb-3">
                  <div className="space-y-2 form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      className="form-control"
                      value={newCampaign.description}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Describe your airdrop campaign..."
                    />
                  </div>
                </div>
                <div className="col-md-12 mb-3">
                  <div className="space-y-2 form-group">
                    <label htmlFor="totalSupply">Total Supply</label>
                    <input
                      id="totalSupply"
                      type="number"
                      className="form-control"
                      value={newCampaign.totalSupply}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          totalSupply: Number(e.target.value),
                        }))
                      }
                      placeholder="10000"
                    />
                  </div>
                </div>
                <div className="col-md-12 mb-3">
                  <div className="space-y-2 form-group">
                    <label>Requirements</label>
                    {newCampaign.requirements.map((req, index) => (
                      <div key={index} className="d-flex  gap-2 mb-2">
                        <input
                          className="form-control"
                          value={req}
                          onChange={(e) =>
                            updateRequirement(index, e.target.value)
                          }
                          placeholder="e.g., Hold minimum 1 SOL"
                        />
                        {newCampaign.requirements.length > 1 && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="cross-btn"
                            onClick={() => removeRequirement(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline-primary"
                      className="btn-style mt-3 transparent"
                      size="sm"
                      onClick={addRequirement}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Requirement
                    </Button>
                  </div>
                </div>
                <div className="col-md-12 mt-3">
                  <div className="d-flex align-items-center space-x-2 mb-2 custom-toggle-switch">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={newCampaign.isActive}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                    />
                    <label htmlFor="isActive" className="toggle-label"></label>
                    <span>Campaign Active</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCreateCampaign}
                disabled={isCreatingCampaign || !newCampaign.name}
                className="w-full mt-3 btn-style  "
                style={{ with: "100%" }}
              >
                {isCreatingCampaign ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Campaign"
                )}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default AdminAirdropManagement;
